import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { STOCK_THRESHOLDS } from '../../data/mockData';

const { FiAlertTriangle, FiAlertCircle, FiPackage } = FiIcons;

/**
 * Derive the stock state from a raw stock number.
 * Returns one of: 'out' | 'critical' | 'low' | 'normal'
 */
export function getStockState(stock) {
  if (stock === 0)                          return 'out';
  if (stock <= STOCK_THRESHOLDS.CRITICAL)   return 'critical';
  if (stock <= STOCK_THRESHOLDS.LOW)        return 'low';
  return 'normal';
}

/**
 * Shared config per state — single source of truth for colours & copy.
 */
const STATE_CONFIG = {
  out: {
    icon: FiPackage,
    label: 'Out of Stock',
    pill: 'bg-zinc-700/90 text-white border-zinc-600',
    bar:  null,
    dot:  'bg-zinc-400',
    pulse: false,
  },
  critical: {
    icon: FiAlertCircle,
    label: (stock) => `Only ${stock} left!`,
    pill: 'bg-red-600/90 text-white border-red-500',
    bar:  'bg-red-500',
    dot:  'bg-red-500',
    pulse: true,
  },
  low: {
    icon: FiAlertTriangle,
    label: (stock) => `Low Stock · ${stock} left`,
    pill: 'bg-amber-500/90 text-white border-amber-400',
    bar:  'bg-amber-400',
    dot:  'bg-amber-400',
    pulse: true,
  },
  normal: null,
};

// ─── Compact pill — used on ProductCard image overlay ─────────────────────────
export const StockPill = ({ stock, className = '' }) => {
  const state = getStockState(stock);
  const cfg   = STATE_CONFIG[state];
  if (!cfg) return null;                          // normal → render nothing

  const label = typeof cfg.label === 'function' ? cfg.label(stock) : cfg.label;

  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border backdrop-blur-sm shadow-lg ${cfg.pill} ${className}`}
    >
      {cfg.pulse && (
        <span className="relative flex h-1.5 w-1.5">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${cfg.dot} opacity-75`} />
          <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${cfg.dot}`} />
        </span>
      )}
      <SafeIcon icon={cfg.icon} className="w-2.5 h-2.5" />
      {label}
    </motion.span>
  );
};

// ─── Inline banner — used on ProductDetail info panel ────────────────────────
export const StockBanner = ({ stock }) => {
  const state = getStockState(stock);
  const cfg   = STATE_CONFIG[state];
  if (!cfg) return null;

  const label = typeof cfg.label === 'function' ? cfg.label(stock) : cfg.label;

  // Width of the stock bar as a percentage (capped at LOW threshold)
  const barPct = cfg.bar
    ? Math.max(4, Math.min(100, (stock / STOCK_THRESHOLDS.LOW) * 100))
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`rounded-2xl border-2 overflow-hidden ${
        state === 'out'      ? 'border-zinc-300 bg-zinc-50' :
        state === 'critical' ? 'border-red-200 bg-red-50'   :
                               'border-amber-200 bg-amber-50'
      }`}
    >
      <div className="flex items-center gap-3 px-5 py-4">
        {/* Pulsing dot */}
        {cfg.pulse && (
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${cfg.dot} opacity-60`} />
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${cfg.dot}`} />
          </span>
        )}
        {!cfg.pulse && (
          <SafeIcon icon={cfg.icon} className={`w-4 h-4 shrink-0 ${
            state === 'out' ? 'text-zinc-500' : ''
          }`} />
        )}

        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-black uppercase tracking-widest ${
            state === 'out'      ? 'text-zinc-600' :
            state === 'critical' ? 'text-red-700'  :
                                   'text-amber-700'
          }`}>
            {label}
          </p>

          {/* Micro stock bar */}
          {cfg.bar && (
            <div className="mt-2 h-1 bg-white/60 rounded-full overflow-hidden w-full">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${barPct}%` }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
                className={`h-full rounded-full ${cfg.bar}`}
              />
            </div>
          )}
        </div>

        {/* Context hint */}
        <p className={`text-[9px] font-light shrink-0 ${
          state === 'out'      ? 'text-zinc-500' :
          state === 'critical' ? 'text-red-500'  :
                                 'text-amber-600'
        }`}>
          {state === 'out'      ? 'Notify me'     :
           state === 'critical' ? 'Order soon'    :
                                  'Selling fast'}
        </p>
      </div>
    </motion.div>
  );
};

export default StockBanner;